import { useMemo, useState } from 'react';

import { getApiErrorMessage, getAssetUrl, uploadAssets } from '../../api/client';
import type { Asset } from '../../api/types';
import AttachmentPicker from '../../components/AttachmentPicker';
import { fileIcon, formatBytes } from '../../utils/ticketDisplay';

type Props = {
  ticketId: string;
  assets: Asset[];
  onRefresh: () => void;
};

function sourceLabel(asset: Asset): string {
  if (!asset.commentId) return 'Description';
  const author = asset.comment?.author;
  if (author) return `Comment by ${author.firstName} ${author.lastName}`;
  const parentName = asset.comment?.parentAuthorName;
  if (parentName) return `Comment by ${parentName}`;
  return 'Comment';
}

function uploaderLabel(asset: Asset): string {
  if (asset.uploadedBy) {
    return `${asset.uploadedBy.firstName} ${asset.uploadedBy.lastName}`;
  }
  return 'Parent';
}

export default function FilesTab({ ticketId, assets, onRefresh }: Props) {
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const grouped = useMemo(() => {
    const desc = assets.filter((a) => !a.commentId);
    const byComment = new Map<string, Asset[]>();
    for (const a of assets.filter((a) => a.commentId)) {
      const key = a.commentId!;
      const list = byComment.get(key) ?? [];
      list.push(a);
      byComment.set(key, list);
    }
    const commentGroups = [...byComment.entries()].sort((a, b) => {
      const ta = a[1][0]?.createdAt ?? '';
      const tb = b[1][0]?.createdAt ?? '';
      return ta.localeCompare(tb);
    });
    return { desc, commentGroups };
  }, [assets]);

  async function download(assetId: string) {
    const { url } = await getAssetUrl(assetId);
    window.open(url, '_blank');
  }

  async function handleUpload() {
    if (pendingFiles.length === 0) return;
    setUploading(true);
    setError('');
    try {
      await uploadAssets(ticketId, pendingFiles);
      setPendingFiles([]);
      onRefresh();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setUploading(false);
    }
  }

  function renderRow(asset: Asset) {
    return (
      <tr key={asset.id}>
        <td>
          <span className="file-cell">
            <span>{fileIcon(asset.mimeType)}</span>
            {asset.fileName}
          </span>
        </td>
        <td>{sourceLabel(asset)}</td>
        <td>{uploaderLabel(asset)}</td>
        <td>{new Date(asset.createdAt).toLocaleString()}</td>
        <td>{formatBytes(asset.sizeBytes)}</td>
        <td>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => void download(asset.id)}
          >
            Download
          </button>
        </td>
      </tr>
    );
  }

  return (
    <div className="files-tab">
      <div className="files-upload-panel card card-body">
        <h3>Upload files</h3>
        <p className="text-muted">
          Attach files to the ticket description. Up to 10 MB total per upload batch.
        </p>
        {error && <div className="alert alert-error">{error}</div>}
        <AttachmentPicker
          id="files-tab-attachments"
          files={pendingFiles}
          onChange={setPendingFiles}
          disabled={uploading}
        />
        {pendingFiles.length > 0 && (
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={uploading}
            onClick={() => void handleUpload()}
          >
            {uploading ? 'Uploading…' : 'Upload'}
          </button>
        )}
      </div>

      {assets.length === 0 ? (
        <div className="empty-state">No files attached.</div>
      ) : (
        <div className="table-wrap">
          <table className="data-table files-table">
            <thead>
              <tr>
                <th>File</th>
                <th>Source</th>
                <th>Uploaded by</th>
                <th>Date</th>
                <th>Size</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {grouped.desc.length > 0 && (
                <>
                  <tr className="files-group-row">
                    <td colSpan={6}>Description</td>
                  </tr>
                  {grouped.desc.map(renderRow)}
                </>
              )}
              {grouped.commentGroups.flatMap(([commentId, rows]) => [
                <tr key={`g-${commentId}`} className="files-group-row">
                  <td colSpan={6}>{sourceLabel(rows[0]!)}</td>
                </tr>,
                ...rows.map(renderRow),
              ])}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
